import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import PublicNavbar from '@/components/layout/PublicNavbar';
import PublicFooter from '@/components/layout/PublicFooter';
import { ArrowRight, Palette, Package, Layers, Sparkles } from 'lucide-react';

export default function DesainKemasanPage() {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <div className="min-h-screen bg-[#FFFDF9] text-[#4A3B32] font-sans selection:bg-[#D4AF37] selection:text-white">
        <PublicNavbar />

        {/* Hero Section */}
        <section className="relative pt-24 pb-32 px-6 lg:px-12 bg-gradient-to-br from-[#FFFDF9] to-[#F9F5EC] overflow-hidden" data-aos="fade-up">
          <div className="absolute right-0 top-0 bottom-0 w-full lg:w-1/2 opacity-20 lg:opacity-100 z-0 hidden md:block">
            <img src="https://images.unsplash.com/photo-1608248543803-ba4f8c70ae0b?q=80&w=1200&auto=format&fit=crop" alt="Cosmetic Packaging" className="w-full h-full object-cover object-left mask-image-gradient-left" />
            <div className="absolute inset-0 bg-gradient-to-r from-[#F9F5EC] via-[#F9F5EC]/80 to-transparent"></div>
          </div>

          <div className="container mx-auto max-w-7xl relative z-10">
            <div className="w-full lg:w-1/2 pt-10">
              <span className="inline-block py-1.5 px-4 bg-[#E6D5B8]/40 text-[#8C6208] text-[10px] font-bold tracking-widest uppercase rounded mb-4">
                LAYANAN DESAIN & KEMASAN
              </span>
              <h1 className="text-4xl md:text-5xl lg:text-5xl font-bold tracking-tight mb-6 leading-[1.15] text-[#302621]">
                Desain & Kemasan <br /> Menarik untuk Brand <br /> Kosmetik Anda
              </h1>
              <p className="text-base text-[#7A695B] mb-10 leading-relaxed max-w-md font-light">
                Kami membantu Anda menciptakan desain dan kemasan yang menarik, fungsional, dan sesuai dengan karakter brand Anda untuk meningkatkan daya saing di pasaran.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button size="lg" className="bg-[#D4AF37] hover:bg-[#B58B22] text-white px-8 py-6 text-base rounded-lg shadow-lg shadow-[#D4AF37]/20 border-0 transition-transform hover:-translate-y-1">
                  Konsultasi Desain <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
                <Button size="lg" variant="outline" className="border-[#302621] text-[#302621] bg-transparent hover:bg-[#302621] hover:text-white px-8 py-6 text-base rounded-lg transition-transform hover:-translate-y-1">
                  Lihat Portofolio
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* 4 Poin Kunci */}
        <section className="relative -mt-16 px-6 lg:px-12 z-20" data-aos="fade-up">
          <div className="container mx-auto max-w-6xl">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
              {[
                { title: "Desain Eksklusif", desc: "Desain unik sesuai identitas visual brand Anda.", icon: <Palette /> },
                { title: "Kemasan Berkualitas", desc: "Material premium dengan kualitas terbaik.", icon: <Package /> },
                { title: "Fungsional & Estetis", desc: "Desain menarik, fungsional, dan ergonomis.", icon: <Layers /> },
                { title: "Tren & Market Fit", desc: "Desain mengikuti tren dan kebutuhan pasar.", icon: <Sparkles /> }
              ].map((feat, idx) => (
                <div key={idx} className="bg-white p-6 md:p-8 rounded-2xl shadow-xl shadow-[#302621]/5 border border-[#E6D5B8]/30 flex flex-col hover:border-[#D4AF37] hover:shadow-md transition-all group">
                  <div className="w-12 h-12 rounded-xl bg-[#F9F5EC] flex items-center justify-center text-[#D4AF37] mb-6 group-hover:scale-110 transition-transform">
                    {feat.icon}
                  </div>
                  <h4 className="font-bold text-[#302621] mb-2 text-sm">{feat.title}</h4>
                  <p className="text-xs text-[#7A695B] leading-relaxed">{feat.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Layanan Desain & Kemasan Kami */}
        <section className="py-24 px-6 lg:px-12 bg-white" data-aos="fade-up">
          <div className="container mx-auto max-w-6xl text-center">
            <h2 className="text-3xl font-bold text-[#302621] mb-4">Layanan Desain & Kemasan Kami</h2>
            <p className="text-[#7A695B] mb-16 max-w-2xl mx-auto">Solusi lengkap untuk mewujudkan tampilan produk yang menarik dan profesional.</p>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { title: "Desain Branding", desc: "Pembuatan logo, palet warna, dan identitas visual yang kuat untuk brand Anda.", img: "https://images.unsplash.com/photo-1596462502278-27bfdc403348?q=80&w=300&auto=format&fit=crop" },
                { title: "Desain Kemasan", desc: "Desain kemasan primer dan sekunder yang menarik perhatian pelanggan.", img: "https://images.unsplash.com/photo-1620916566398-39f1143ab7be?q=80&w=300&auto=format&fit=crop" },
                { title: "Pemilihan Material", desc: "Rekomendasi bahan kemasan terbaik (kaca, akrilik, PET) sesuai jenis produk.", img: "https://images.unsplash.com/photo-1576426863848-c21f53c60b19?q=80&w=300&auto=format&fit=crop" },
                { title: "Prototipe & Mockup", desc: "Pembuatan visualisasi 3D atau sampel fisik sebelum produksi massal.", img: "https://images.unsplash.com/photo-1556228578-0d85b1a4d571?q=80&w=300&auto=format&fit=crop" }
              ].map((svc, idx) => (
                <div key={idx} className="bg-[#FCFAF6] border border-[#E6D5B8]/40 p-4 rounded-2xl flex flex-col text-left hover:border-[#D4AF37] hover:shadow-sm transition-all group cursor-pointer">
                  <div className="w-full aspect-[4/3] bg-gray-100 rounded-xl mb-6 overflow-hidden relative">
                    <img src={svc.img} alt={svc.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  </div>
                  <h4 className="font-bold text-[#302621] mb-2 text-base">{svc.title}</h4>
                  <p className="text-xs text-[#7A695B] leading-relaxed">{svc.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Proses Desain & Pengembangan Kemasan */}
        <section className="py-24 px-6 lg:px-12 bg-[#FCFAF6] border-y border-[#E6D5B8]/30" data-aos="fade-up">
          <div className="container mx-auto max-w-6xl text-center">
            <h2 className="text-3xl font-bold text-[#302621] mb-4">Proses Desain & Pengembangan Kemasan</h2>
            <p className="text-[#7A695B] mb-16 max-w-2xl mx-auto">Proses kreatif dan terstruktur untuk menghasilkan desain kemasan yang sempurna.</p>
            
            <div className="grid grid-cols-2 md:grid-cols-3 gap-8 relative max-w-4xl mx-auto">
              {[
                { no: "01", title: "Konsultasi Visual & Branding", desc: "Diskusi ide, palet warna, dan identitas brand" },
                { no: "02", title: "Pemilihan Material Kemasan", desc: "Rekomendasi jenis botol, jar, dan box" },
                { no: "03", title: "Sketsa & Konsep Desain 2D", desc: "Pembuatan draf desain awal untuk direview" },
                { no: "04", title: "Pembuatan 3D Mockup", desc: "Visualisasi digital hasil desain secara realistis" },
                { no: "05", title: "Evaluasi & Revisi Desain", desc: "Penyempurnaan desain berdasarkan feedback" },
                { no: "06", title: "Cetak Sampel (Proofing)", desc: "Pembuatan sampel fisik sebelum mass-pro" }
              ].map((step, idx) => (
                <div key={idx} className="relative z-10 flex flex-col items-center w-full mb-4">
                  <div className="w-12 h-12 rounded-full bg-white border-2 border-[#D4AF37] shadow-sm flex items-center justify-center text-[#8C6208] font-bold text-sm mb-4">
                    {step.no}
                  </div>
                  <h4 className="font-bold text-[#302621] text-sm mb-2 text-center">{step.title}</h4>
                  <p className="text-[11px] text-[#7A695B] text-center leading-relaxed px-2">{step.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Inspirasi Desain Kemasan */}
        <section className="py-24 px-6 lg:px-12 bg-white" data-aos="fade-up">
          <div className="container mx-auto max-w-6xl text-center">
            <h2 className="text-3xl font-bold text-[#302621] mb-4">Inspirasi Desain Kemasan</h2>
            <p className="text-[#7A695B] mb-12 max-w-2xl mx-auto">Beberapa contoh desain kemasan elegan yang dapat kami kembangkan untuk brand Anda.</p>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
              {[
                "https://images.unsplash.com/photo-1629198688000-71f23e745b6e?q=80&w=400&auto=format&fit=crop",
                "https://images.unsplash.com/photo-1600880292203-757bb62b4baf?q=80&w=400&auto=format&fit=crop",
                "https://images.unsplash.com/photo-1610832958506-aa56368176cf?q=80&w=400&auto=format&fit=crop",
                "https://images.unsplash.com/photo-1594035910387-fea47794261f?q=80&w=400&auto=format&fit=crop"
              ].map((imgUrl, idx) => (
                <div key={idx} className="rounded-2xl overflow-hidden aspect-square border border-[#E6D5B8]/40 group cursor-pointer relative">
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors z-10"></div>
                  <img src={imgUrl} alt={`Packaging Inspiration ${idx + 1}`} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 px-6 lg:px-12 bg-white border-t border-[#E6D5B8]/30" data-aos="fade-up">
          <div className="container mx-auto max-w-6xl">
            <div className="bg-[#EFE8D8] rounded-3xl overflow-hidden relative shadow-sm border border-[#E6D5B8]/50">
              <div className="absolute right-0 top-0 bottom-0 w-1/2 opacity-100 mix-blend-multiply hidden md:block">
                <img src="/Gambar Web/Botol berdiri.png" alt="Packaging CTA" className="w-full h-full object-cover mask-image-gradient-left" />
              </div>
              <div className="relative z-10 p-12 md:p-16 w-full md:w-[55%] lg:w-1/2">
                <h2 className="text-3xl md:text-4xl font-extrabold text-[#302621] mb-4 leading-tight">Buat Brand Anda Lebih Berkesan!</h2>
                <p className="text-[#7A695B] text-lg leading-relaxed mb-8">
                  Desain dan kemasan yang tepat akan membuat produk Anda tampil profesional dan menarik minat pelanggan.
                </p>
                <Button size="lg" className="bg-[#D4AF37] hover:bg-[#B58B22] text-white px-8 py-4 text-base rounded-lg shadow-md border-0 transition-transform hover:-translate-y-1 font-semibold flex items-center w-fit">
                  Konsultasi Sekarang <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Footer */}
        <PublicFooter />
      </div>
    </>
  );
}
